import { toLocalYMD } from '../utils/hydration';

/**
 * ⭐ A tab that comes back shows TODAY as the server has it (docs/DAY_LOG_STALE_WRITES.md,
 * "The screen catches up on focus"). The day-log stores load once per page; their
 * writes are safe (read-in-write + compare-and-swap), but a tab left open still SHOWED
 * the day it loaded — another device's drinks or ticks, or yesterday after midnight —
 * until it wrote to that day or reloaded.
 *
 * When the page becomes visible again (or the window gets focus), every store that has
 * loaded re-reads today's row — one small select each, through the store's own write
 * queue, so it lands after any write in flight and before any write tapped after it.
 * The store shows it with this tab's unsaved changes on top and moves `todayDate` to
 * the calendar's today.
 *
 * ⚠️⚠️ READ ONLY. A refresh never writes: not the day's row, not a linked habit, no XP.
 * Throttled per store: due when its last read of today is ≥ REFRESH_AFTER_MS old, or
 * when the calendar day has changed since (a tab woken just after midnight).
 */

export const REFRESH_AFTER_MS = 60_000;

interface Registered {
  refresh: () => Promise<unknown>;
  /** When the store last read today, and which day that was. null = not loaded (never refreshed). */
  last: { at: number; date: string } | null;
}

const registered = new Set<Registered>();

/**
 * Register a store's "re-read today". It is never called until the store says it has
 * read today (`read`, from its load and from each refresh) — a store that hasn't loaded,
 * or was cleared on sign-out (`forget`), is left alone.
 */
export function refreshTodayOnFocus(refresh: () => Promise<unknown>) {
  const entry: Registered = { refresh, last: null };
  registered.add(entry);
  return {
    read(date: string) { entry.last = { at: Date.now(), date }; },
    forget() { entry.last = null; },
  };
}

/** Refresh every loaded store that is due. Resolves when they have all answered. */
export async function refreshDueDayLogs(): Promise<void> {
  const now = Date.now();
  const today = toLocalYMD(new Date(now));
  const due = [...registered].filter(
    e => e.last && (e.last.date !== today || now - e.last.at >= REFRESH_AFTER_MS),
  );
  // Counted as read from now: `visibilitychange` and `focus` usually fire together.
  for (const e of due) e.last = { at: now, date: today };
  await Promise.all(due.map(e => e.refresh().catch(err => console.error('Error refreshing today:', err))));
}

let stop: (() => void) | null = null;

/** Start listening (main.tsx). Idempotent; returns the stop. */
export function startDayLogFocusRefresh(): () => void {
  if (stop) return stop;
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};
  const onVisible = () => { if (document.visibilityState === 'visible') void refreshDueDayLogs(); };
  const onFocus = () => { void refreshDueDayLogs(); };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onFocus);
  stop = () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onFocus);
    stop = null;
  };
  return stop;
}
